/**
 * GHL Qualifier — Location Detail page.
 *
 * Hosts the Qualified Leads / Converted Leads tabs for a single location.
 * URL shape: `/admin/ghl-qualifier/location/:locationId/:tab` where `tab` is
 * one of `QUALIFIER_DETAIL_TABS`. Unknown tabs redirect to the default.
 */

import { useEffect, useState } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { TimeRangeFilter } from '@/components/ghl-dashboard'
import {
  useQualifiedLeadsFeed,
  useConvertedLeadsFeed,
  usePhoneCallLeadsFeed,
  useQualifierStats,
  useQualifierLocationOverview,
  useQualifierGoogleAdsSettings,
} from '@/hooks/useGHLQualifierDashboard'
import { useUrlTimeRange } from '@/hooks/useUrlTimeRange'
import { ArrowLeft, RefreshCw, Settings } from 'lucide-react'
import { QualifiedLeadsTab } from '@/components/ghl-qualifier/QualifiedLeadsTab'
import { ConvertedLeadsTab } from '@/components/ghl-qualifier/ConvertedLeadsTab'
import { PhoneCallLeadsTab } from '@/components/ghl-qualifier/PhoneCallLeadsTab'
import { QualifierStatCards } from '@/components/ghl-qualifier/QualifierStatCards'
import { QualifierGoogleAdsSettingsDialog } from '@/components/ghl-qualifier/QualifierGoogleAdsSettingsDialog'
import {
  QUALIFIER_DETAIL_DEFAULT_TAB,
  isQualifierDetailTab,
  type QualifierDetailTab,
} from '@/lib/ghl-qualifier'

const PAGE_SIZE = 50

export default function GHLQualifierDashboardPage() {
  const { locationId, tab } = useParams<{ locationId: string; tab: string }>()
  const navigate = useNavigate()
  const location = useLocation()

  const activeTab: QualifierDetailTab = isQualifierDetailTab(tab)
    ? tab
    : QUALIFIER_DETAIL_DEFAULT_TAB

  // Rewrite the URL when the tab segment is invalid so the address bar matches
  // what's rendered. `replace: true` so we don't pollute browser history.
  useEffect(() => {
    if (!locationId) return
    if (tab && !isQualifierDetailTab(tab)) {
      navigate(
        {
          pathname: `/admin/ghl-qualifier/location/${locationId}/${QUALIFIER_DETAIL_DEFAULT_TAB}`,
          search: location.search,
        },
        { replace: true }
      )
    }
  }, [tab, locationId, navigate, location.search])

  const [timeRange, setTimeRange] = useUrlTimeRange()
  const [qualifiedPage, setQualifiedPage] = useState(0)
  const [gaSettingsOpen, setGaSettingsOpen] = useState(false)
  const [qualifiedOutcome, setQualifiedOutcome] = useState('all')
  const [qualifiedSearch, setQualifiedSearch] = useState('')
  const [convertedPage, setConvertedPage] = useState(0)
  const [convertedStatus, setConvertedStatus] = useState('all')
  const [convertedSearch, setConvertedSearch] = useState('')
  const [phoneCallPage, setPhoneCallPage] = useState(0)
  const [phoneCallStatus, setPhoneCallStatus] = useState('all')
  const [phoneCallSearch, setPhoneCallSearch] = useState('')

  const setActiveTab = (newTab: string) => {
    if (!locationId || !isQualifierDetailTab(newTab)) return
    navigate(
      {
        pathname: `/admin/ghl-qualifier/location/${locationId}/${newTab}`,
        search: location.search,
      },
      { replace: true }
    )
  }

  const stats = useQualifierStats(timeRange, locationId)
  // Overview includes per-location toggle states + names. Reused here so we
  // can show the location name + the qualifier-off banner without an extra
  // table query per page load.
  const overview = useQualifierLocationOverview(timeRange)
  const locationRow = overview.data?.find((r) => r.location_id === locationId)
  const locationName = locationRow?.location_name ?? locationId ?? 'Location'

  // Per-contact "Test upload" buttons show only when (a) this location delivers
  // directly to Google Ads, and (b) the per-location "Show Test upload button"
  // toggle is on (set via the Google Ads settings dialog). Independent of the
  // "Test mode" toggle, which only controls whether the cron auto-uploads.
  const gaSettings = useQualifierGoogleAdsSettings(locationId ?? '', !!locationId)
  const directMode = gaSettings.data?.conversion_delivery_method === 'google_ads'
  const testButtonEnabled = gaSettings.data?.test_button_enabled === true
  const showTestButton = directMode && testButtonEnabled
  // The GHL Phone Call Leads tab is only relevant for phone-call-only
  // locations (their qualified/converted jobs are suppressed, so this is their
  // only contact feed). `gaSettingsLoaded` gates the redirect below so we don't
  // bounce off the tab before the setting has resolved.
  const gaSettingsLoaded = gaSettings.isSuccess
  const phoneCallOnly = gaSettings.data?.phone_call_only === true

  const qualifiedFeed = useQualifiedLeadsFeed(
    timeRange,
    qualifiedPage,
    locationId,
    qualifiedOutcome,
    PAGE_SIZE,
    activeTab === 'qualified-leads'
  )
  const convertedFeed = useConvertedLeadsFeed(
    timeRange,
    convertedPage,
    locationId,
    convertedStatus,
    PAGE_SIZE,
    activeTab === 'converted-leads'
  )
  const phoneCallFeed = usePhoneCallLeadsFeed(
    timeRange,
    phoneCallPage,
    locationId,
    phoneCallStatus,
    PAGE_SIZE,
    activeTab === 'phone-call-leads' && phoneCallOnly
  )

  // Clamp out-of-range page params so refreshes don't show "Page 5 of 1".
  useEffect(() => {
    if (qualifiedFeed.isLoading || !qualifiedFeed.data) return
    const totalPages = Math.max(1, Math.ceil(qualifiedFeed.data.count / PAGE_SIZE))
    if (qualifiedPage > totalPages - 1) setQualifiedPage(0)
  }, [qualifiedFeed.isLoading, qualifiedFeed.data, qualifiedPage])

  useEffect(() => {
    if (convertedFeed.isLoading || !convertedFeed.data) return
    const totalPages = Math.max(1, Math.ceil(convertedFeed.data.count / PAGE_SIZE))
    if (convertedPage > totalPages - 1) setConvertedPage(0)
  }, [convertedFeed.isLoading, convertedFeed.data, convertedPage])

  useEffect(() => {
    if (phoneCallFeed.isLoading || !phoneCallFeed.data) return
    const totalPages = Math.max(1, Math.ceil(phoneCallFeed.data.count / PAGE_SIZE))
    if (phoneCallPage > totalPages - 1) setPhoneCallPage(0)
  }, [phoneCallFeed.isLoading, phoneCallFeed.data, phoneCallPage])

  const refresh = async () => {
    const refetches: Promise<unknown>[] = [stats.refetch(), overview.refetch()]
    if (activeTab === 'qualified-leads') refetches.push(qualifiedFeed.refetch())
    if (activeTab === 'converted-leads') refetches.push(convertedFeed.refetch())
    if (activeTab === 'phone-call-leads') refetches.push(phoneCallFeed.refetch())
    await Promise.allSettled(refetches)
  }

  const isRefreshing =
    stats.isFetching ||
    overview.isFetching ||
    (activeTab === 'qualified-leads' && qualifiedFeed.isFetching) ||
    (activeTab === 'converted-leads' && convertedFeed.isFetching) ||
    (activeTab === 'phone-call-leads' && phoneCallFeed.isFetching)

  function handleQualifiedRowClick(id: string) {
    navigate({
      pathname: `/admin/ghl-qualifier/qualified/${id}`,
      search: location.search,
    })
  }

  function handleConvertedRowClick(id: string) {
    navigate({
      pathname: `/admin/ghl-qualifier/converted/${id}`,
      search: location.search,
    })
  }

  function handlePhoneCallRowClick(id: string) {
    navigate({
      pathname: `/admin/ghl-qualifier/phone-call/${id}`,
      search: location.search,
    })
  }

  // If we land on the phone-call-leads tab for a location that isn't
  // phone-call-only, redirect to the default tab (the trigger is hidden, so the
  // URL shouldn't render hidden content).
  useEffect(() => {
    if (!locationId || !gaSettingsLoaded) return
    if (activeTab === 'phone-call-leads' && !phoneCallOnly) {
      navigate(
        {
          pathname: `/admin/ghl-qualifier/location/${locationId}/${QUALIFIER_DETAIL_DEFAULT_TAB}`,
          search: location.search,
        },
        { replace: true }
      )
    }
  }, [activeTab, phoneCallOnly, gaSettingsLoaded, locationId, navigate, location.search])

  // The two tab triggers can show count badges from the location overview row
  // without waiting for a feed query — saves an empty-state flicker when the
  // operator switches tabs. Falls back to '—' until overview resolves.
  const qualifiedCount = locationRow?.qualified_total ?? null
  const convertedCount = locationRow?.converted_total ?? null
  const phoneCallCount = locationRow?.form_call_total ?? null

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={() => navigate({ pathname: '/admin/ghl-qualifier', search: location.search })}
            aria-label="Back to qualifier locations"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{locationName}</h1>
            <p className="text-sm text-muted-foreground">
              GHL Qualifier — Location ID:{' '}
              <span className="font-mono text-xs">{locationId}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setGaSettingsOpen(true)}
            disabled={!locationId}
            className="gap-2"
            aria-label="Google Ads conversion settings"
          >
            <Settings className="h-3.5 w-3.5" />
            Google Ads
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={isRefreshing}
            className="gap-2"
            aria-label="Refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <TimeRangeFilter value={timeRange} onChange={setTimeRange} />
        </div>
      </div>

      {locationId && (
        <QualifierGoogleAdsSettingsDialog
          open={gaSettingsOpen}
          onOpenChange={setGaSettingsOpen}
          locationId={locationId}
          locationName={locationName}
        />
      )}

      {/*
        Per-location spend row (Claude + Whisper). Renders above the tabs so
        operators can answer "is this customer profitable?" at a glance.
        Scoped to the current location and time window via the same
        useQualifierStats() result the tabs read.
      */}
      <QualifierStatCards
        mode="location-spend"
        stats={stats.data}
        isLoading={stats.isLoading}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="qualified-leads">
            Qualified Leads
            {qualifiedCount != null && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                ({qualifiedCount.toLocaleString()})
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="converted-leads">
            Converted Leads
            {convertedCount != null && (
              <span className="ml-1.5 text-xs text-muted-foreground">
                ({convertedCount.toLocaleString()})
              </span>
            )}
          </TabsTrigger>
          {phoneCallOnly && (
            <TabsTrigger value="phone-call-leads">
              GHL Phone Call Leads
              {phoneCallCount != null && (
                <span className="ml-1.5 text-xs text-muted-foreground">
                  ({phoneCallCount.toLocaleString()})
                </span>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="qualified-leads" className="space-y-4">
          <QualifiedLeadsTab
            stats={stats.data}
            statsLoading={stats.isLoading}
            data={qualifiedFeed.data?.data ?? []}
            count={qualifiedFeed.data?.count ?? 0}
            page={qualifiedPage}
            pageSize={PAGE_SIZE}
            isLoading={qualifiedFeed.isLoading}
            outcomeFilter={qualifiedOutcome}
            searchTerm={qualifiedSearch}
            onPageChange={setQualifiedPage}
            onOutcomeChange={(o) => {
              setQualifiedOutcome(o)
              setQualifiedPage(0)
            }}
            onSearchChange={setQualifiedSearch}
            onRowClick={handleQualifiedRowClick}
            showTestButton={showTestButton}
            showUploadStatus={directMode}
          />
        </TabsContent>

        <TabsContent value="converted-leads" className="space-y-4">
          <ConvertedLeadsTab
            stats={stats.data}
            statsLoading={stats.isLoading}
            data={convertedFeed.data?.data ?? []}
            count={convertedFeed.data?.count ?? 0}
            page={convertedPage}
            pageSize={PAGE_SIZE}
            isLoading={convertedFeed.isLoading}
            statusFilter={convertedStatus}
            searchTerm={convertedSearch}
            onPageChange={setConvertedPage}
            onStatusChange={(s) => {
              setConvertedStatus(s)
              setConvertedPage(0)
            }}
            onSearchChange={setConvertedSearch}
            onRowClick={handleConvertedRowClick}
            showTestButton={showTestButton}
            showUploadStatus={directMode}
          />
        </TabsContent>

        {phoneCallOnly && (
          <TabsContent value="phone-call-leads" className="space-y-4">
            <PhoneCallLeadsTab
              data={phoneCallFeed.data?.data ?? []}
              count={phoneCallFeed.data?.count ?? 0}
              page={phoneCallPage}
              pageSize={PAGE_SIZE}
              isLoading={phoneCallFeed.isLoading}
              statusFilter={phoneCallStatus}
              searchTerm={phoneCallSearch}
              onPageChange={setPhoneCallPage}
              onStatusChange={(s) => {
                setPhoneCallStatus(s)
                setPhoneCallPage(0)
              }}
              onSearchChange={setPhoneCallSearch}
              onRowClick={handlePhoneCallRowClick}
            />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}
